import { TokenAuthMutation } from "../documents/user/mutation.generated";
import { ApolloClient, makeVar } from "@apollo/client";
import AsyncStorage from "@react-native-async-storage/async-storage";

import dayjs from "dayjs";

type TokenAuthProp = TokenAuthMutation["user"]["tokenAuth"];

type LoginProps = Pick<
  TokenAuthProp,
  "payload" | "refreshExpiresIn" | "refreshToken" | "token"
>;

export const isLoggedIn = makeVar<boolean | null>(null);

/**
 * Incremented every time the websocket (re)connects.
 *
 * Server-side subscription events are fire-and-forget: anything published while
 * we were disconnected is gone for good, and no amount of reconnecting will
 * replay it. So a feature that cannot afford to miss an event — a streaming agent
 * run, say — watches this and re-reads its state from the server whenever it
 * changes. Set from the `connected` handler in `client.ts`.
 */
export const wsGeneration = makeVar(0);

/**
 * Drops the websocket so it reconnects under the current identity.
 *
 * The socket authenticates ONCE, in `connection_init`, and the server pins the
 * connection to that user for its whole life. Since the socket is now a long-lived
 * singleton, signing in or out has to physically drop it — otherwise every
 * subscription opened afterwards keeps resolving as the previous user. `client.ts`
 * registers the real implementation; this indirection only exists to keep the
 * import graph acyclic (`client.ts` imports this module, never the reverse).
 */
let terminateWebSocket: (() => void) | undefined;

export const registerWebSocketTerminator = (terminate: () => void) => {
  terminateWebSocket = terminate;
};

export const logIn = async (props: LoginProps, client?: ApolloClient) => {
  const { token, refreshToken, refreshExpiresIn, payload } = props;
  await AsyncStorage.setItem("token", token);
  await AsyncStorage.setItem("refreshToken", refreshToken);
  await AsyncStorage.setItem("refreshExpiresIn", refreshExpiresIn.toString());
  const tokenExpiresIn = payload.exp - payload.origIat;
  const expiration = dayjs().unix() + tokenExpiresIn - 60;
  await AsyncStorage.setItem("tokenExpiration", expiration.toString());
  isLoggedIn(true);
  // The token is written before this runs, so the reconnect picks up the new one.
  terminateWebSocket?.();
  client?.resetStore();
};
export const getToken = async () => AsyncStorage.getItem("token");

export const logOut = async (client?: ApolloClient) => {
  await AsyncStorage.removeItem("token");
  await AsyncStorage.removeItem("refreshToken");
  await AsyncStorage.removeItem("refreshExpiresIn");
  await AsyncStorage.removeItem("tokenExpiration");
  isLoggedIn(false);
  terminateWebSocket?.();
  client?.clearStore();
};

export const getRefreshToken = async () => AsyncStorage.getItem("refreshToken");

export const hasTokenExpired = async () => {
  const expires = await AsyncStorage.getItem("tokenExpiration");
  if (expires === null) return true;
  return dayjs().unix() > Number(expires);
};

export const getFlows = async () => {
  const flows = await AsyncStorage.getItem("flows");
  if (flows === null) {
    return {};
  }
  return JSON.parse(flows);
};

export const setFlows = async (flows: object) => {
  await AsyncStorage.setItem("flows", JSON.stringify(flows));
};

export const setFlowDone = async (flowName: string) => {
  const oldFlows = await getFlows();
  const flows = { oldFlows, [flowName]: true };
  setFlows(flows);
};

export const getFlow = async (flowName: string) => {
  const flows = await getFlows();
  const flow = flows[flowName];
  return flow || false;
};
